/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import { Genre, logger } from '@musedrops/shared';
import { GenreApi } from '../../../services/genreApi';

interface GenreSelectorProps {
  selectedGenreId?: string;
  onGenreChange: (genreId: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  width?: 'full' | 'medium' | 'small';
}

export const GenreSelector: React.FC<GenreSelectorProps> = ({
  selectedGenreId,
  onGenreChange,
  required = false,
  error = false,
  helperText,
  disabled = false,
  width = 'full',
}) => {
  const [genres, setGenres] = useState<Genre[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadGenres = async () => {
      try {
        setLoading(true);
        const genreList = await GenreApi.getAll();
        setGenres(genreList);
        setLoadError(null);
      } catch (err) {
        logger.error('Failed to load genres:', err);
        setLoadError('Failed to load genres');
      } finally {
        setLoading(false);
      }
    };

    loadGenres();
  }, []);

  const getWidth = () => {
    switch (width) {
      case 'small':
        return 200;
      case 'medium':
        return 300;
      case 'full':
      default:
        return '100%';
    }
  };

  if (loadError) {
    return (
      <Typography color="error" variant="body2">
        {loadError}
      </Typography>
    );
  }

  return (
    <Box sx={{ width: getWidth() }}>
      <FormControl fullWidth error={error} disabled={disabled}>
        <InputLabel id="genre-select-label">Genre{required && ' *'}</InputLabel>
        <Select
          labelId="genre-select-label"
          value={selectedGenreId || ''}
          label={`Genre${required ? ' *' : ''}`}
          onChange={e => onGenreChange(e.target.value)}
          disabled={loading || disabled}
        >
          <MenuItem value="">
            <em>No genre selected</em>
          </MenuItem>
          {genres.map(genre => (
            <MenuItem key={genre.id} value={genre.id}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {genre.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {genre.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <Typography
            variant="caption"
            color={error ? 'error' : 'text.secondary'}
            sx={{ mt: 0.5 }}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};
